/**
 * Meta pixel helpers shared by V2 and V3: ad-click attribution kept from the first arrival,
 * advanced matching for the pixel, and the per-checkout record that decides which browser
 * event a Stripe return fires. Server twin: vivideo-backend stripe-webhook (_meta_capi).
 *
 * Dedup contract: the checkout's txn_id is the browser eventID AND the server event_id
 * (create-checkout-session metadata meta_event_id, or the txn_id in the success_url).
 */
import { sha256Hex } from './sha256';

export const META_PIXEL_ID = '2166346390858509';

const ATTRIBUTION_KEY = 'vivideo_ad_attribution';
/** Read by index.html's pixel init (advanced matching only applies to the first init on a page). */
export const MATCHING_KEY = 'vivideo_meta_am';
const CHECKOUT_PREFIX = 'vivideo_meta_checkout_';
const CHECKOUT_TTL_MS = 2 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmKey = (typeof UTM_KEYS)[number];
type Touch = Partial<Record<UtmKey, string>> & { ts: number };

export interface AdAttribution {
  first?: Touch;
  last?: Touch;
  fbclid?: string;
  fbclid_ts?: number;
  /** Latest Google Ads click (one of gclid / gbraid / wbraid); gclid_ts is its capture time (ms). */
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  gclid_ts?: number;
  /** Latest Microsoft Ads click (msclkid) and its capture time (ms). */
  msclkid?: string;
  msclkid_ts?: number;
}

const GOOGLE_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
type GoogleClickKey = (typeof GOOGLE_CLICK_KEYS)[number];
/** Older Google clicks are not sent with a checkout (the conversion action's 30-day click window). */
export const GOOGLE_CLICK_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Older Microsoft clicks are not sent with a checkout (the offline conversion goal's 30-day window). */
export const MICROSOFT_CLICK_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const safeGet = (storage: Storage | undefined, key: string): string | null => {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
};
const safeSet = (storage: Storage | undefined, key: string, value: string): void => {
  try {
    storage?.setItem(key, value);
  } catch {
    /* storage blocked */
  }
};
const local = (): Storage | undefined => (typeof window !== 'undefined' ? window.localStorage : undefined);

export function getAdAttribution(): AdAttribution {
  try {
    const raw = safeGet(local(), ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as AdAttribution) : {};
  } catch {
    return {};
  }
}

/**
 * Keep fbclid / gclid / gbraid / wbraid / msclkid / utm_* from the arrival URL (the landing forwards them).
 * Call before Amplitude scrubs utm_* from the URL. first is write-once; last and click ids move
 * with each new ad touch.
 */
export function captureAdAttribution(href: string = window.location.href, now: number = Date.now()): AdAttribution {
  const state = getAdAttribution();
  try {
    const params = new URL(href).searchParams;
    const touch: Touch = { ts: now };
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) touch[k] = v.slice(0, 200);
    }
    const fbclid = params.get('fbclid');
    const google = GOOGLE_CLICK_KEYS.filter((k) => params.get(k));
    const msclkid = params.get('msclkid');
    const hasUtm = UTM_KEYS.some((k) => touch[k]);
    if (!hasUtm && !fbclid && !google.length && !msclkid) return state;
    if (hasUtm) {
      state.first = state.first ?? touch;
      state.last = touch;
    }
    if (fbclid) {
      state.fbclid = fbclid.slice(0, 500);
      state.fbclid_ts = now;
    }
    if (google.length) {
      // A new Google click replaces the previous one whole.
      for (const k of GOOGLE_CLICK_KEYS) delete state[k];
      for (const k of google) state[k] = (params.get(k) as string).slice(0, 500);
      state.gclid_ts = now;
    }
    if (msclkid) {
      state.msclkid = msclkid.slice(0, 500);
      state.msclkid_ts = now;
    }
    safeSet(local(), ATTRIBUTION_KEY, JSON.stringify(state));
  } catch {
    /* bad URL or storage blocked: attribution is best-effort */
  }
  return state;
}

function readCookie(name: string): string | undefined {
  try {
    const hit = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : undefined;
  } catch {
    return undefined;
  }
}

/** _fbp / _fbc cookies (set by the pixel on .vivideo.ai), else fbc rebuilt from the kept fbclid. */
export function metaClickIds(): { fbp?: string; fbc?: string; fbclid?: string } {
  const a = getAdAttribution();
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc') || (a.fbclid ? `fb.1.${a.fbclid_ts ?? Date.now()}.${a.fbclid}` : undefined);
  const out: { fbp?: string; fbc?: string; fbclid?: string } = {};
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  if (a.fbclid) out.fbclid = a.fbclid;
  return out;
}

/** `GCL.<seconds>.<id>` (_gcl_aw / _gcl_gb set by the Google tag on .vivideo.ai). */
function readGclCookie(name: string): { id: string; ts: number } | undefined {
  const parts = readCookie(name)?.split('.') ?? [];
  if (parts.length < 3 || parts[0] !== 'GCL') return undefined;
  const ts = Number(parts[1]) * 1000;
  const id = parts.slice(2).join('.');
  return id && Number.isFinite(ts) ? { id, ts } : undefined;
}

/**
 * The latest Google Ads click: the one kept from the URL, or the Google tag's cookie when newer.
 * gclid_ts is the click's time in ms. Empty when none was seen.
 */
export function googleClickIds(): Partial<Record<GoogleClickKey, string>> & { gclid_ts?: number } {
  const a = getAdAttribution();
  const kept = GOOGLE_CLICK_KEYS.filter((k) => a[k]);
  let best: Partial<Record<GoogleClickKey, string>> & { gclid_ts?: number } = {};
  if (kept.length) {
    for (const k of kept) best[k] = a[k];
    best.gclid_ts = a.gclid_ts;
  }
  for (const [cookie, key] of [['_gcl_aw', 'gclid'], ['_gcl_gb', 'gbraid']] as const) {
    const c = readGclCookie(cookie);
    if (c && c.ts > (best.gclid_ts ?? 0)) best = { [key]: c.id, gclid_ts: c.ts };
  }
  return best;
}

/** create-checkout-session body fields: the latest Google click ids, when recent enough. */
export function checkoutGoogleFields(now: number = Date.now()): Record<string, string> {
  const { gclid_ts: ts, ...ids } = googleClickIds();
  if (!ts || now - ts > GOOGLE_CLICK_MAX_AGE_MS) return {};
  return ids as Record<string, string>;
}

/** create-checkout-session body field: the latest Microsoft Ads click id, when recent enough. */
export function checkoutMicrosoftFields(now: number = Date.now()): Record<string, string> {
  const { msclkid, msclkid_ts: ts } = getAdAttribution();
  if (!msclkid || !ts || now - ts > MICROSOFT_CLICK_MAX_AGE_MS) return {};
  return { msclkid };
}

/** get-user-id `ad_attribution` (stored write-once on the user); undefined when nothing was kept. */
export function signupAdAttribution(): Record<string, string> | undefined {
  const a = getAdAttribution();
  const out: Record<string, string> = {};
  const touch = a.first ?? a.last;
  for (const k of UTM_KEYS) if (touch?.[k]) out[k] = touch[k] as string;
  if (touch?.ts) out.landing_ts = String(touch.ts);
  if (a.fbclid) {
    out.fbclid = a.fbclid;
    if (a.fbclid_ts) out.fbclid_ts = String(a.fbclid_ts);
  }
  const ids = metaClickIds();
  if (ids.fbc) out.fbc = ids.fbc;
  if (ids.fbp) out.fbp = ids.fbp;
  const { gclid_ts: gts, ...google } = googleClickIds();
  for (const [k, v] of Object.entries(google)) if (v) out[k] = v;
  if (gts && Object.keys(google).length) out.gclid_ts = String(gts);
  if (a.msclkid) {
    out.msclkid = a.msclkid;
    if (a.msclkid_ts) out.msclkid_ts = String(a.msclkid_ts);
  }
  return Object.keys(out).length ? out : undefined;
}

/** create-checkout-session body fields: the shared event id plus click ids and the latest UTMs. */
export function checkoutMetaFields(eventId?: string | null): Record<string, string> {
  const out: Record<string, string> = { ...metaClickIds() } as Record<string, string>;
  const last = getAdAttribution().last;
  for (const k of UTM_KEYS) if (last?.[k]) out[k] = last[k] as string;
  if (eventId) out.meta_event_id = eventId;
  return out;
}

export function txnIdFromUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url, 'https://app.vivideo.ai').searchParams.get('txn_id');
  } catch {
    return null;
  }
}

export interface MetaCheckout {
  /** Same rule as the webhook: still Free and never subscribed. undefined = backend did not say. */
  first?: boolean;
  /** What Stripe will charge first, in major units, and its currency (ISO, upper case). */
  value?: number;
  currency?: string;
  at?: number;
}

/** Kept before the Stripe redirect; read back on the return to /subscription?success=true&txn_id=. */
export function rememberMetaCheckout(eventId: string, record: MetaCheckout): void {
  const store = local();
  try {
    if (store) {
      const now = Date.now();
      for (let i = store.length - 1; i >= 0; i--) {
        const k = store.key(i);
        if (!k?.startsWith(CHECKOUT_PREFIX)) continue;
        const at = (JSON.parse(store.getItem(k) || '{}') as MetaCheckout).at ?? 0;
        if (now - at > CHECKOUT_TTL_MS) store.removeItem(k);
      }
    }
  } catch {
    /* pruning is best-effort */
  }
  safeSet(store, CHECKOUT_PREFIX + eventId, JSON.stringify({ ...record, at: Date.now() }));
}

export function takeMetaCheckout(eventId: string): MetaCheckout | null {
  const store = local();
  const raw = safeGet(store, CHECKOUT_PREFIX + eventId);
  if (!raw) return null;
  try {
    store?.removeItem(CHECKOUT_PREFIX + eventId);
  } catch {
    /* keep going */
  }
  try {
    return JSON.parse(raw) as MetaCheckout;
  } catch {
    return null;
  }
}

/** The client-side first-subscription rule (V3, whose checkout server does not report one). */
export const isFirstSubscriptionPlan = (plan?: string | null): boolean =>
  !plan || plan.trim().toLowerCase() === 'free';

/**
 * Advanced matching: hashed em + external_id, kept for index.html's pixel init on every later
 * page load (Meta reads user data only from the first init of a page). Same hashes as the CAPI.
 */
export function rememberMetaMatching(email?: string | null, userId?: string | null): void {
  const data: Record<string, string> = {};
  const em = (email || '').trim().toLowerCase();
  const id = (userId || '').trim();
  if (em) data.em = sha256Hex(em);
  if (id) data.external_id = sha256Hex(id);
  if (!Object.keys(data).length) return;
  safeSet(local(), MATCHING_KEY, JSON.stringify(data));
}

export function clearMetaMatching(): void {
  try {
    local()?.removeItem(MATCHING_KEY);
  } catch {
    /* storage blocked */
  }
}
